'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import Toast from '@/components/Toast';

const TOAST_ID = 'profile-card-action-toast';

function ProfileCardActions() {
  const [showToast, setShowToast] = React.useState(false);

  function handleClickButton() {
    // If we’re already showing the toast and they click the button again, we should retrigger it. This could be useful in case the user missed the animation the first time and tries the button again.
    // In order to retrigger, we unmount the toast, wait a couple of frames, and re-mount it.
    if (showToast) {
      setShowToast(false);
      window.setTimeout(() => {
        setShowToast(true);
      }, 100);
      return;
    }

    // We display multiple ProfileCards at the same time, and each one has its own `showToast` state. The “proper” solution here is to use context to share a single state variable, but I'm feeling lazy. I'll check to see if another card’s toast is showing, and if so, we’ll do nothing.
    const isAnotherCardShowingToast = document.querySelector(
      `[data-toast-id="${TOAST_ID}"]`
    );

    if (isAnotherCardShowingToast) {
      return;
    }

    setShowToast(true);
  }
  return (
    <>
      <Actions>
        <SecondaryAction onClick={handleClickButton}>
          Follow
        </SecondaryAction>
        <PrimaryAction onClick={handleClickButton}>
          Message
        </PrimaryAction>
      </Actions>

      {showToast && (
        <Toast
          id={TOAST_ID}
          type="error"
          title="Not real buttons"
          handleDismiss={() => setShowToast(false)}
          popTimeout={0}
        >
          These buttons don’t actually do anything, they’re just for
          show!
        </Toast>
      )}
    </>
  );
}

const Actions = styled.div`
  grid-area: actions;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 16px;

  @container (min-width: 20rem) {
    flex-direction: row;
  }
`;

const Action = styled.button`
  display: block;
  width: 100%;
  font-size: 1rem;
  padding-block: 0.5rem;
  border-radius: 4px;
  text-align: center;
  font-weight: var(--font-weight-medium);
  user-select: none;
`;
const PrimaryAction = styled(Action)`
  position: relative;
  background: hsl(240deg 90% 45%);
  color: white;
  transition: background 200ms;
  overflow: clip;

  &:hover {
    background: hsl(240deg 95% 55%);
  }

  &::after {
    content: '';
    position: absolute;
    top: -150px;
    left: 0;
    right: 0;
    margin-inline: auto;
    width: 100%;
    aspect-ratio: 1 / 1;
    filter: blur(42px);
    background: #f6afff;
    border-radius: 50%;
    mix-blend-mode: screen;
    opacity: 0;
    transition: opacity 300ms;
  }

  &:hover::after {
    opacity: 0.333;
  }
`;
const SecondaryAction = styled(Action)`
  background: transparent;
  border: 2px solid var(--color-gray-500);
  color: var(--color-gray-700);
  transition: all 200ms;

  &:hover {
    border: 2px solid var(--color-gray-700);
    color: var(--color-text);
  }
`;

export default ProfileCardActions;
